'use client';

import React, { useState, useEffect, useCallback } from 'react';

// ─── PORTFOLIO SECTION NAV ────────────────────────────────────────────────────

const SECTIONS = [
  { id: 'photographs', label: 'Photographs', icon: '◈', color: '#c9a84c', count: 27 },
  { id: 'graphics', label: 'Graphics', icon: '◈', color: '#8a8ab8', count: 16 },
  { id: '3d', label: '3D', icon: '◎', color: '#2d8a87', count: 25 },
  { id: 'new-nature', label: 'New Nature', icon: '✦', color: '#ffa200', count: null },
  { id: 'monolyth-vr', label: 'VR Environments', icon: '◎', color: '#2d8a87', count: null },
  { id: 'process', label: 'Process / Glyphs', icon: '⬡', color: '#2d7a47', count: null },
  { id: 'cultural-center', label: 'Cultural Center', icon: '◈', color: '#c9a84c', count: null },
  { id: 'serena-williams', label: 'Serena Williams Design Crew 3D', icon: '✦', color: '#ffa200', count: null, isChapter: true },
];

function getScrollProgress(): number {
  const scrollTop = window.scrollY;
  const docHeight = document.documentElement.scrollHeight - window.innerHeight;
  return docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0;
}

function getActiveSection(): string | null {
  for (let i = SECTIONS.length - 1; i >= 0; i--) {
    const el = document.getElementById(SECTIONS[i].id);
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.top <= 120) return SECTIONS[i].id;
    }
  }
  return null;
}

export function PortfolioSectionNav() {
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  const handleScroll = useCallback(() => {
    setScrollProgress(getScrollProgress());
    setActiveSection(getActiveSection());
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setPanelOpen(false);
    }
  };

  const activeSec = SECTIONS.find(s => s.id === activeSection);
  const activeIndex = SECTIONS.findIndex(s => s.id === activeSection);

  // Circumference for progress ring
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - scrollProgress);

  return (
    <>
      {/* ── DESKTOP: Sticky horizontal top nav ── */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(6,13,18,0.97)',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
        padding: '0 7.1%',
        display: 'flex', gap: 0, alignItems: 'center',
        backdropFilter: 'blur(8px)',
        overflowX: 'auto',
      }}>
        <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.1em', textTransform: 'uppercase', marginRight: 16, flexShrink: 0 }}>
          Sections:
        </span>
        {SECTIONS.map(sec => (
          <button
            key={sec.id}
            onClick={() => scrollTo(sec.id)}
            style={{
              padding: '12px 14px',
              background: 'none', border: 'none',
              cursor: 'pointer',
              fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
              color: activeSection === sec.id ? sec.color : 'rgba(212,197,169,0.5)',
              display: 'flex', alignItems: 'center', gap: 5,
              transition: 'color 0.15s',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              borderBottom: activeSection === sec.id ? `2px solid ${sec.color}` : '2px solid transparent',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = sec.color)}
            onMouseLeave={e => (e.currentTarget.style.color = activeSection === sec.id ? sec.color : 'rgba(212,197,169,0.5)')}
          >
            <span style={{ color: sec.color }}>{sec.icon}</span>
            {sec.label}
            {sec.count !== null && (
              <span style={{ fontSize: 7, color: 'rgba(212,197,169,0.3)' }}>({sec.count})</span>
            )}
          </button>
        ))}
      </div>

      {/* ── MOBILE: Floating button + slide panel ── */}
      {/* Floating trigger button */}
      <button
        onClick={() => setPanelOpen(v => !v)}
        aria-label="Portfolio sections navigation"
        style={{
          position: 'fixed',
          bottom: 24,
          right: 20,
          zIndex: 200,
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: 'rgba(6,13,18,0.97)',
          border: `1px solid ${activeSec?.color ?? 'rgba(201,168,76,0.3)'}`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 4px 20px rgba(0,0,0,0.5), 0 0 0 1px ${activeSec?.color ?? 'rgba(201,168,76,0.15)'}`,
          backdropFilter: 'blur(8px)',
          padding: 0,
        }}
      >
        {/* Progress ring */}
        <svg
          width="52"
          height="52"
          style={{ position: 'absolute', top: 0, left: 0, transform: 'rotate(-90deg)' }}
        >
          <circle
            cx="26"
            cy="26"
            r={radius}
            fill="none"
            stroke="rgba(201,168,76,0.12)"
            strokeWidth="2"
          />
          <circle
            cx="26"
            cy="26"
            r={radius}
            fill="none"
            stroke={activeSec?.color ?? '#c9a84c'}
            strokeWidth="2"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.3s ease' }}
          />
        </svg>
        {/* Icon / counter */}
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          {panelOpen ? (
            <span style={{ color: '#c9a84c', fontSize: 14 }}>✕</span>
          ) : (
            <>
              <div style={{ color: activeSec?.color ?? '#c9a84c', fontSize: 11, fontFamily: 'monospace', letterSpacing: '.04em', lineHeight: 1 }}>
                {activeIndex >= 0 ? activeIndex + 1 : '—'}
              </div>
              <div style={{ color: 'rgba(212,197,169,0.3)', fontSize: 7, fontFamily: 'monospace', letterSpacing: '.06em' }}>
                /{SECTIONS.length}
              </div>
            </>
          )}
        </div>
      </button>

      {/* Slide-out panel */}
      {panelOpen && (
        <div
          style={{
            position: 'fixed',
            bottom: 84,
            right: 16,
            zIndex: 199,
            width: 280,
            maxHeight: '70vh',
            overflowY: 'auto',
            background: 'rgba(6,13,18,0.98)',
            border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 12,
            boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
            backdropFilter: 'blur(12px)',
          }}
        >
          {/* Panel header */}
          <div style={{
            padding: '16px 18px 12px',
            borderBottom: '1px solid rgba(201,168,76,0.1)',
            position: 'sticky',
            top: 0,
            background: 'rgba(6,13,18,0.98)',
            zIndex: 1,
          }}>
            <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.14em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 4 }}>
              ◈ Portfolio Sections
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                flex: 1,
                height: 2,
                background: 'rgba(201,168,76,0.1)',
                borderRadius: 1,
                overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: `${scrollProgress * 100}%`,
                  background: 'linear-gradient(90deg, #c9a84c, #ffa200)',
                  borderRadius: 1,
                  transition: 'width 0.3s ease',
                }} />
              </div>
              <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.4)', flexShrink: 0 }}>
                {Math.round(scrollProgress * 100)}%
              </span>
            </div>
          </div>

          {/* Section list */}
          <div style={{ padding: '8px 0' }}>
            {SECTIONS.map((sec, idx) => {
              const isActive = activeSection === sec.id;
              return (
                <React.Fragment key={sec.id}>
                  {/* Chapter break indicator */}
                  {sec.isChapter && (
                    <div style={{
                      margin: '8px 18px',
                      borderTop: `1px solid ${sec.color}40`,
                      paddingTop: 8,
                    }}>
                      <span style={{
                        fontFamily: 'monospace',
                        fontSize: 7,
                        letterSpacing: '.14em',
                        textTransform: 'uppercase',
                        color: `${sec.color}60`,
                      }}>
                        ◈ New Chapter
                      </span>
                    </div>
                  )}
                  <button
                    onClick={() => scrollTo(sec.id)}
                    style={{
                      width: '100%',
                      padding: '10px 18px',
                      background: isActive ? `${sec.color}10` : 'none',
                      border: 'none',
                      borderLeft: `3px solid ${isActive ? sec.color : 'transparent'}`,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      textAlign: 'left',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => {
                      if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = `${sec.color}08`;
                    }}
                    onMouseLeave={e => {
                      if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'none';
                    }}
                  >
                    {/* Dot indicator */}
                    <div style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: isActive ? sec.color : 'transparent',
                      border: `2px solid ${isActive ? sec.color : 'rgba(212,197,169,0.2)'}`,
                      flexShrink: 0,
                      boxShadow: isActive ? `0 0 8px ${sec.color}60` : 'none',
                      transition: 'all 0.2s',
                    }} />

                    {/* Section info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: 'monospace',
                        fontSize: 9,
                        letterSpacing: '.08em',
                        textTransform: 'uppercase',
                        color: isActive ? sec.color : 'rgba(212,197,169,0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        transition: 'color 0.15s',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        <span style={{ color: sec.color, flexShrink: 0 }}>{sec.icon}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{sec.label}</span>
                      </div>
                    </div>

                    {/* Count badge */}
                    {sec.count !== null && (
                      <span style={{
                        fontFamily: 'monospace',
                        fontSize: 7,
                        color: isActive ? sec.color : 'rgba(212,197,169,0.25)',
                        background: isActive ? `${sec.color}15` : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${isActive ? `${sec.color}30` : 'rgba(255,255,255,0.06)'}`,
                        borderRadius: 10,
                        padding: '2px 6px',
                        flexShrink: 0,
                        transition: 'all 0.15s',
                      }}>
                        {sec.count}
                      </span>
                    )}

                    {/* Section number */}
                    <span style={{
                      fontFamily: 'monospace',
                      fontSize: 8,
                      color: 'rgba(212,197,169,0.2)',
                      flexShrink: 0,
                    }}>
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>

          {/* Panel footer */}
          <div style={{
            padding: '12px 18px',
            borderTop: '1px solid rgba(201,168,76,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.06em' }}>
              Monolyth (VR) · 2018–2021
            </span>
            <a
              href="https://www.behance.net/gallery/112291665/Monolyth-(VR)"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.4)', textDecoration: 'none', letterSpacing: '.06em' }}
              onClick={() => setPanelOpen(false)}
            >
              Behance →
            </a>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {panelOpen && (
        <div
          onClick={() => setPanelOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 198,
            background: 'transparent',
          }}
        />
      )}
    </>
  );
}
